import { useEffect, useState } from 'react';
import type { FC } from 'react';

import { useContent } from '@eneco/cms';
import logger from '@eneco/common/log';
import { stripAccolades } from '@eneco/common/string';
import { useFormsPublicV2PostB2BForm } from '@eneco/dc/hooks';
import useDC from '@eneco/dc/useDC';
import type { SubmitHandler } from 'react-hook-form';

import { GenericFormShell } from './generic-form/GenericFormShell';
import { prepareRevolutionFormRequest } from './generic-form/request';
import type { FormValues } from './generic-form/types';
import type { RevolutionFormContent } from './revolution-form-content.types';
import { REVOLUTION_FORM_SLOTS } from './slots';

const getFormId = (senderIdOption: RevolutionFormContent['senderIdOption']) => {
  // A per-environment id is a Sitecore droplink item; the adapter lifts its value up a level.
  const formId = typeof senderIdOption === 'string' ? senderIdOption : senderIdOption?.value;

  if (!formId) {
    throw new Error('Sender id is required');
  }
  return formId;
};

/**
 * `RevolutionForm` block for the CMS-agnostic lane, mirroring
 * `libs/sitecore/components/forms/src/GenericForm/RevolutionForm.tsx`: a generic form whose fields
 * sit in `jss-revolution-form`, posted to the Revolution CRM (B2B form) endpoint.
 *
 * As in legacy, a missing form id throws while rendering, and a failed send is only logged: legacy
 * resets its error type to `undefined` on every failure, so it never shows a notification, and the
 * authored `submitErrorNotification` goes unused on both lanes.
 */
const RevolutionForm: FC = () => {
  const content = useContent<RevolutionFormContent>();
  const { label, businessUnit } = useDC();
  const { error, isError, isSuccess, send } = useFormsPublicV2PostB2BForm();

  const formId = getFormId(content.senderIdOption);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Legacy issue, kept: logged only, no notification for the visitor.
  useEffect(() => {
    if (isError) logger.error('DpV4I2', 'Failed to submit form', { error });
  }, [isError, error]);

  const submitForm: SubmitHandler<FormValues> = async values => {
    setIsSubmitting(true);
    try {
      await send({
        businessUnit,
        formId: stripAccolades(formId),
        label,
        requestBody: prepareRevolutionFormRequest(formId, values),
        recaptchaEnterpriseToken: values.recaptchaToken,
      } as never);
    } catch (submitError) {
      logger.error('DpV4I2', 'Failed to submit form', { error: submitError });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <GenericFormShell
      slots={REVOLUTION_FORM_SLOTS.main}
      onSubmit={submitForm}
      isSubmitting={isSubmitting}
      isSuccess={isSuccess}
      // No fallback text, and a redirect even without an address, as in legacy.
      buttonText={content.formSubmitRedirectLink?.text}
      redirectWithoutHref
    />
  );
};

export default RevolutionForm;
